<script setup lang="ts">
import { computed, onMounted, ref, watch, onBeforeMount } from 'vue'
import { useRuntimeConfig } from 'nuxt/app'
import { useAuthStore } from '../stores/authStore'
import { useStartWizardStore, type WizardStatus } from '../stores/startWizardStore'
import { useOnboardingStore, ONBOARDING_ELEMENTS, DismissalType } from '../stores/onboardingStore'
import { useModeStore } from '../stores/mode'
import { useCentralConfigStore } from '../stores/centralConfigStore'
import { useLogger } from '../composables/useLogger'

const config = useRuntimeConfig()
const logger = useLogger()
const { t } = useI18n()

const sections = computed(() => [
  {
    title: t('home.sections.welcome.title'),
    description: t('home.sections.welcome.description'),
    icon: 'i-heroicons-home'
  },
])

const authStore = useAuthStore()
const startWizardStore = useStartWizardStore()
const onboardingStore = useOnboardingStore()
const modeStore = useModeStore()
const centralConfigStore = useCentralConfigStore()

const { isHideExtendedMenu } = storeToRefs(centralConfigStore)
const { mode } = storeToRefs(modeStore)

// Ensure central config is initialized
onBeforeMount(async () => {
  await centralConfigStore.ensureInitialized()
})

onMounted(async () => {
  await startWizardStore.loadStatus()
  await startWizardStore.checkAllInProgressTiles()
})

function getWizardTiles() {
  const base = []
  base.push(

    {
      id: 'calendar',
      title: t('home.wizard.tiles.calendar.title'),
      description: t('home.wizard.tiles.calendar.description'),
      icon: 'i-heroicons-calendar',
      navigateTo: '/startwizard/sw-calendar'
    }
  )

  const extended = [
      {
        id: 'album',
        title: t('home.wizard.tiles.album.title'),
        description: t('home.wizard.tiles.album.description'),
      icon: 'i-heroicons-photo',
      navigateTo: '/startwizard/sw-album'
      },
      {
        id: 'jigsaw',
        title: t('home.wizard.tiles.jigsaw.title'),
        description: t('home.wizard.tiles.jigsaw.description'),
        icon: 'i-heroicons-puzzle-piece',
        navigateTo: '/startwizard/sw-jigsaw'
      }
      
    ]

  if (!isHideExtendedMenu.value) {
    base.push(...extended)
  }

  base.push({
          id: 'qrcode',
          title: t('home.wizard.tiles.qrcode.title'),
          description: t('home.wizard.tiles.qrcode.description'),
          icon: 'i-heroicons-device-tablet',
          navigateTo: '/startwizard/sw-qrcode'
      })

  return base
}

const filteredWizardTiles = computed(() => {
  return getWizardTiles().filter(tile => getTileStatus(tile.id) !== 'not_relevant')
})

function getTileStatus(id: string): WizardStatus {
  return startWizardStore.status[id]?.status || 'not_relevant'
}

function getTileDoneAt(id: string): Date | null {
  return startWizardStore.status[id]?.doneAt || null
}
</script>

<template>
  <UDashboardPage>
    <UDashboardPanel grow>
      <UDashboardNavbar :title="t('navigation.sections.home.label')">
        <template #right>
        </template>
      </UDashboardNavbar>

      <UDashboardPanelContent>
        <div class="p-6">
          <div v-if="startWizardStore.isLoaded && startWizardStore.hasInProgressTiles">
            <div class="text-center mb-6">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ $t('home.wizard.header.title') }}
              </h2>
              <p class="mt-2 text-gray-600 dark:text-gray-400">
                {{ $t('home.wizard.header.description') }}
              </p>
            </div>
            <div class="p-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <StartWizardTile v-for="tile in filteredWizardTiles" :key="tile.id" v-bind="tile"
                  :status="getTileStatus(tile.id)" :done-at="getTileDoneAt(tile.id)" />
              </div>
            </div>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div v-for="(section, index) in sections" :key="index"
              class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 flex flex-col">
              <div class="flex items-center mb-4">
                <UIcon :name="section.icon" class="text-primary mr-3" size="lg" />
                <h3 class="text-lg font-semibold">{{ section.title }}</h3>
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 flex-grow">{{ section.description }}</p>
            </div>
          </div>
        </div>
      </UDashboardPanelContent>
    </UDashboardPanel>
  </UDashboardPage>
</template>
